import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { StepUpAction, StepUpStatus } from '@/lib/step-up'

/**
 * KAN-857 U8 — the step-up pre-flight for one action (design §5.7 step 1).
 * Only fetched while `enabled` (a destructive dialog is open). A failed
 * pre-flight is treated as "not required": the server still enforces, and a
 * refused submit falls back to the refusal path.
 */
export const stepUpStatusKey = (action: StepUpAction) => ['admin', 'step-up-status', action] as const

const NOT_REQUIRED: StepUpStatus = { required: false, active: false, expiresAt: null, unavailable: false }

export function useStepUpStatus(action: StepUpAction, enabled: boolean) {
  const query = useQuery({
    queryKey: stepUpStatusKey(action),
    queryFn: () =>
      api.get<StepUpStatus>(`/auth/step-up/status?${new URLSearchParams({ action }).toString()}`),
    enabled,
    staleTime: 0,
    retry: false,
  })
  // A failed (re)fetch is "not required", even if React Query still holds an
  // earlier required/unavailable result (codex r1): the server enforces.
  return { status: query.isError ? NOT_REQUIRED : (query.data ?? NOT_REQUIRED), isLoading: enabled && query.isLoading }
}

/** Re-check after a refusal (the window may have lapsed while the dialog was open). */
export function useRefreshStepUpStatus() {
  const qc = useQueryClient()
  return (action: StepUpAction) => qc.invalidateQueries({ queryKey: stepUpStatusKey(action) })
}
